import type { MutationResult, ReasonInput, StatusReasonInput } from '../../api/contract';
import { createRepositoryToken } from '../../data/RepositoryProvider';
import type { PageResult } from '../../shared/data/types';
import type {
  ChangePartnerStatusInput,
  CreatePartnerInput,
  CredentialExpiryInput,
  IpAllowlistEntry,
  IpAllowlistInput,
  IssueCredentialInput,
  OneTimeSecretResult,
  PartnerCredential,
  PartnerDetail,
  PartnerListQuery,
  PartnerSummary,
  UpdatePartnerInput,
} from './types';

export interface PartnerRepository {
  list(query: PartnerListQuery, signal: AbortSignal): Promise<PageResult<PartnerSummary>>;
  get(partnerId: string, signal: AbortSignal): Promise<PartnerDetail>;
  create(input: CreatePartnerInput): Promise<MutationResult<PartnerDetail>>;
  update(partnerId: string, input: UpdatePartnerInput): Promise<MutationResult<PartnerDetail>>;
  changeStatus(
    partnerId: string,
    input: ChangePartnerStatusInput,
  ): Promise<MutationResult<PartnerDetail>>;
  listCredentials(partnerId: string, signal: AbortSignal): Promise<readonly PartnerCredential[]>;
  issueCredential(partnerId: string, input: IssueCredentialInput): Promise<OneTimeSecretResult>;
  rotateCredential(
    partnerId: string,
    credentialId: string,
    input: ReasonInput,
  ): Promise<OneTimeSecretResult>;
  changeCredentialExpiry(
    partnerId: string,
    credentialId: string,
    input: CredentialExpiryInput,
  ): Promise<MutationResult<PartnerCredential>>;
  revokeCredential(
    partnerId: string,
    credentialId: string,
    input: ReasonInput,
  ): Promise<MutationResult<PartnerCredential>>;
  listIpAllowlist(partnerId: string, signal: AbortSignal): Promise<readonly IpAllowlistEntry[]>;
  createIpAllowlistEntry(
    partnerId: string,
    input: IpAllowlistInput,
  ): Promise<MutationResult<IpAllowlistEntry>>;
  updateIpAllowlistEntry(
    partnerId: string,
    entryId: string,
    input: IpAllowlistInput,
  ): Promise<MutationResult<IpAllowlistEntry>>;
  changeIpAllowlistStatus(
    partnerId: string,
    entryId: string,
    input: StatusReasonInput,
  ): Promise<MutationResult<IpAllowlistEntry>>;
}

export const partnerRepositoryToken = createRepositoryToken<PartnerRepository>('partners');

export const defaultPartnerListQuery: PartnerListQuery = Object.freeze<PartnerListQuery>({
  page: 1,
  pageSize: 25,
  sort: 'updatedAt',
  order: 'desc',
  filters: { code: '', name: '', status: '' as const, currency: '' },
});
